import { Async, F2 } from '@client/new/helpers/types';
import { useAsyncQuery } from '@client/new/helpers/gql';
import * as g from 'generated/graphql';
import { CommentsData, CreateCommentParams } from './comments.domain';
import { toCommentsData } from './comments.mappers';
import { useInfluencerId } from '@client/new/contexts/InfluencerIdContext';
import { useEffect } from 'react';

type CommentsModelParams = {
  contentId: string | null;
};

type CommentsModel = {
  data: Async<CommentsData>;
  toggleVisibility: F2<string, boolean, Promise<void>>;
  createComment: (params: CreateCommentParams) => Promise<void>;
  deleteComment: (commentId: string) => Promise<void>;
  refetch: () => Promise<void>;
  isMutationRunning: boolean;
};

export const useCommentsModel = (p: CommentsModelParams): CommentsModel => {
  const shouldFetch = !!p.contentId;
  const creatorId = useInfluencerId();

  const { data, refetch, softRefetch } = useAsyncQuery(
    g.useGetCommentsQuery({
      variables: { contentId: p.contentId || '', usersLimit: 5 },
      skip: !shouldFetch,
    }),
    'getComments',
    (comments) => toCommentsData(comments, p.contentId || ''),
    { comments: [], contentId: '' },
  );

  const [updateCommentVisibilityMutation, { loading: isUpdateRunning }] =
    g.useUpdateCommentVisibilityMutation();

  const [createCommentMutation, { loading: isCreateRunning }] =
    g.useCreateCommentMutation();

  const [deleteEngagementMutation, { loading: isDeleteRunning }] =
    g.useDeleteEngagementMutation();

  const isMutationRunning =
    isUpdateRunning || isCreateRunning || isDeleteRunning;

  const toggleVisibility: CommentsModel['toggleVisibility'] = async (
    commentId,
    isVisible,
  ) => {
    await updateCommentVisibilityMutation({
      variables: {
        creatorId: String(creatorId || ''),
        commentId,
        isVisible,
      },
    });
    await softRefetch();
  };

  const createComment: CommentsModel['createComment'] = async (params) => {
    await createCommentMutation({
      variables: {
        creatorId: String(creatorId || ''),
        input: {
          contentId: params.contentId,
          text: params.text,
          threadId: params.threadId,
        },
      },
    });
    await softRefetch();
  };

  const deleteComment: CommentsModel['deleteComment'] = async (commentId) => {
    await deleteEngagementMutation({
      variables: {
        creatorId: String(creatorId || ''),
        engagementId: commentId,
      },
    });
    await softRefetch();
  };

  const refetchComments = async () => {
    await refetch();
  };

  useEffect(() => {
    if (!shouldFetch) return;

    const intervalId = setInterval(() => {
      softRefetch();
    }, 60000);

    return () => {
      clearInterval(intervalId);
    };
  }, [shouldFetch, softRefetch]);

  if (!shouldFetch) {
    return {
      data: { type: 'not-fetched' },
      toggleVisibility,
      createComment,
      deleteComment,
      refetch: refetchComments,
      isMutationRunning,
    };
  }

  return {
    data,
    toggleVisibility,
    createComment,
    deleteComment,
    refetch: refetchComments,
    isMutationRunning,
  };
};
